"use client";

import { unwrapResult } from "@reduxjs/toolkit";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";

import useGtmQuestionSet from "@/app/common-hooks/gtm/use-gtm-question-set";
import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import UnSavedChangesWarningMessage from "@/components/organisms/un-saved-changes-warning-message/un-saved-changes-warning-message";
import ValidateMedicalsWarningMessage from "@/components/organisms/validate-medicals-warning-message/validate-medicals-warning-message";
import withApplication from "@/components/with-application";
import withMedicalFlow from "@/components/with-medical-flow-v2";
import {
  QuestionEight,
  QuestionFour,
  TravellerEdit,
  OrganizerEdit,
  RenewalPage,
} from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import { PRODUCT_DOMAIN } from "@/constants";
import useReplaceRouterPath from "@/hooks/use-replace-router-path/use-replace-router-path";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import useTurnstile from "@/hooks/use-turnstile/use-turnstile";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { useAppDispatch } from "@/store/hook";
import { openHelpOverlay } from "@/store/slices/help-overlay.slice";
import {
  createQuoteOnQuestion,
  updateQuoteOnQuestion,
} from "@/store/slices/quote/quote.action";
import {
  setQuestionData,
  setTurnstileToken,
} from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { QuotePayload } from "@/types/quote/quote-data";
import { Traveller as QuoteTraveller, Traveller } from "@/types/Traveller";
import { TravellerDetails } from "@/types/TravellerDetails";
import {
  ErrorObjEdited,
  FormDataType,
  TravellerContainerProps,
} from "@/types/TravellerType";
import { TripDetails } from "@/types/TripDetails";
import selectLoationHookFlash from "@/utils/common.utils";
import { findAnswerQThreeById } from "@/utils/cruise.utils";
import { showMessenger } from "@/utils/cxone.utils";
import { getNationalityPopularCountryList } from "@/utils/filtered-nationality.utils";
import {
  modifyOrganiserTraveller,
  hidePersonalDetailsFields,
} from "@/utils/organiser.utils";
import { smoothScrollToRef } from "@/utils/scrollUtils";
import {
  generateFromArray,
  generateTravelerDetails,
  getTravellerForm,
  modifiedExsitingArray,
  validateFrom,
} from "@/utils/traveller.utils";
import { getCurrentToken, resetToken } from "@/utils/turnstile/turnstile.utils";

import QuestionBodySection from "./traveller-answer/traveller-form";
import ActionButton from "../../action-button/action-button";
import ConfirmationsModal from "../../quote-section/edit-quote-section/confirmation-modal";
import editQuoteStyle from "../../quote-section/edit-quote-section/edit-quote.module.css";
import BackToRenewal from "../../renewals/back-to-renewal/back-to-renewal";

const initialPayload: QuotePayload = {
  type: "Quote",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    lastVisitedPage: "test",
  },
};

function TravellerContainer({
  usagePage,
  travellerType,
  travellerDetailsEdit,
  pageContent,
  ctaRef,
  formRef,
  newMedicalFlowEnabled,
  medicalTermsAndConditions,
  application,
}: Readonly<TravellerContainerProps>) {
  const {
    questionText = "Question Text",
    ctaButtonText = "Button Text",
    inputOnePlaceholder,
    inputTwoPlaceholder,
    removeButtonLabel,
    addButtonLabel,
    inputOneErrorNumericvalues,
    inputOneErrorSpecialCharacters,
    inputOneErrorMaximumNumber,
    inputTwoErrorNumericvalues,
    inputTwoErrorSpecialCharacters,
    inputTwoErrorMaximumNumber,
    dobMinAge,
    dobMaxAge,
    popupMessage,
    questionTwoText,
    dobPlaceholder,
    coupleMinAge,
    personalStatusPlaceholder,
    nationalityPlaceholder,
    nationalityPopularCountryList,
    ageParentFamily,
    ageFamilyFullTimeEducation,
    ageGroupUnderTwo,
    ageSingleParentFamily,
    ageSingleParentFullTimeEducation,
    cancelEditingBtnText,
    // Identification field content (Spain only)
    identificationTypePlaceholder,
    identificationNumberPlaceholder,
    identificationTypeRequiredError,
    identificationNumberInvalidError,
    identificationNumberWarning,
    identificationTypeDNILabel,
    identificationTypeNIELabel,
    identificationTypePassportLabel,
    medicalQuestionText,
    medicalQuestionInfoText,
    medicalServiceDeniedText,
    medicalYesBtnText,
    medicalNoBtnText,
    travellerAlertMessageIncompleteSectionSingle,
    travellerAlertMessageIncompleteSection,
    travellerAlertMessageLinkSectionSingle,
    travellerAlertMessageLinkSection,
  } = pageContent;
  const router = useRouter();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(TravellerEdit);
  const organiserEdit = searchParams.get(OrganizerEdit);
  const { current, wizard } = useWizard();
  const { t } = useWizardTranslation();
  const dispatch = useAppDispatch();
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { tripDetails, organiser } = quoteResponse.attributes;
  const inputRef = useRef<HTMLHeadingElement | null>(null);
  const { setSelectedState, setHasMedicalConditionState } = useQuestionsState();
  const [travelFormData, setTravelFormData] = useState<{
    formCount: number;
    multipleForm: boolean;
  }>({
    formCount: 1,
    multipleForm: false,
  });
  const [payload, modifyPayload] = useState<QuotePayload>(initialPayload);
  const [isFormSubmit, setIsFormSubmit] = useState(true);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [validationTrigger, setValidationTrigger] = useState<number>(0);
  const [hasMedicalConditions, setHasMedicalConditions] = useState<
    boolean | undefined
  >(undefined);
  const [userSavedTraveller, setUserSavedTraveller] = useState<
    FormDataType[] | undefined
  >(undefined);
  const [isSubmitBtnClicked, setSubmitBtnClicked] = useState<boolean>(false);
  const [errorObjEdited, setErrorObjEdited] = useState<
    ErrorObjEdited | undefined
  >(undefined);
  const [editModal, setEditModal] = useState(false);
  const { resetQuestionData } = useResetStateTravelerEdit();

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const pathname = usePathname();
  useReplaceRouterPath(pathname);
  const isAddOrganizerToTravellerList =
    searchParams.get(TravellerEdit) || searchParams.get(OrganizerEdit);
  const [isOpenTravelerExceedModal, setIsOpenTravelerExceedModal] =
    useState(false);

  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);

  const [travelersLimit, setTravelersLimit] = useState<number>(() =>
    !organiser?.travelling ? 9 : 8
  );

  const { attributes } = quoteResponse;

  const { isTurnstileEnabled } = useTurnstile();

  // Generate fresh turnstile token on mount
  useEffect(() => {
    if (isTurnstileEnabled) {
      resetToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isValidateMedicalModalOpen, setIsValidateMedicalModalOpen] =
    useState<boolean>(false);

  const [experimentDetailsPayload, setExperimentDetailsPayload] =
    useState<Record<string, string | null | undefined> | null>(null);

  useEffect(() => {
    let newLimit = !organiser?.travelling ? 9 : 8;
    const existingTravelerCount =
      attributes?.travellerDetails?.traveller?.length;

    if (existingTravelerCount && newLimit < existingTravelerCount) {
      newLimit = !organiser?.travelling
        ? existingTravelerCount
        : existingTravelerCount - 1;
    }

    setTravelersLimit((prevLimit) =>
      prevLimit !== newLimit ? newLimit : prevLimit
    );
  }, [attributes, attributes?.travellerDetails?.traveller?.length, organiser]);

  useEffect(() => {
    if (travellerType && organiser) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = getTravellerForm(travellerType, organiser?.travelling);
      setTravelFormData(data);
    }
  }, [travellerType, organiser]);

  useEffect(() => {
    if (
      travellerDetailsEdit &&
      travellerDetailsEdit.length > 0 &&
      (organiser?.travelling === true || organiser?.travelling === false)
    ) {
      const organiserTravellerId = sessionStorage.getItem(
        "organiserTravellerId"
      );

      let travellersWithOutOrganiser = [...travellerDetailsEdit];
      if (
        organiser?.travelling ||
        (!organiser?.travelling && organiserTravellerId)
      ) {
        travellersWithOutOrganiser = modifyOrganiserTraveller(
          travellersWithOutOrganiser,
          organiserTravellerId || ""
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = modifiedExsitingArray(
        travellersWithOutOrganiser,
        travellerType || "",
        organiser?.travelling
      );
      const covertToForm = generateFromArray(
        data,
        travellerType,
        tripDetails?.startDate,
        current?.uiElementControls?.getAgeFromPolicyStartForAMT,
        tripDetails?.policyType
      );
      setUserSavedTraveller(covertToForm);
    }
  }, [
    organiser,
    travellerDetailsEdit,
    travellerType,
    tripDetails?.startDate,
    tripDetails?.policyType,
    current?.uiElementControls?.getAgeFromPolicyStartForAMT,
  ]);

  useEffect(() => {
    if (userSavedTraveller) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = validateFrom(
        userSavedTraveller || [],
        travellerType || "",
        organiser?.travelling,
        inputOneErrorNumericvalues,
        inputOneErrorSpecialCharacters,
        inputOneErrorMaximumNumber,
        inputTwoErrorNumericvalues || "",
        inputTwoErrorSpecialCharacters || "",
        inputTwoErrorMaximumNumber || "",
        ageGroupUnderTwo || "",
        dobMinAge || "",
        dobMaxAge || "",
        coupleMinAge || "",
        ageSingleParentFamily || "",
        ageSingleParentFullTimeEducation || "",
        ageParentFamily || "",
        ageFamilyFullTimeEducation || "",
        wizard
      );

      setErrorObjEdited(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSavedTraveller, travellerType]);

  const accessFormData = (formData: FormDataType[]) => {
    if (formData && formData.length > 0) {
      setIsFormSubmit(false);
      setSelectedState(true);
      setUserSavedTraveller(formData);

      // Get experimentDetails from Redux
      const experimentDetailsFromBackend =
        quoteResponse.attributes.experimentDetails;

      // Build experimentDetails for Spain variant users
      const experimentDetails = {
        ...experimentDetailsFromBackend,
        deferOrganiserDetailsFlow: hidePersonalDetailsFields(
          experimentDetailsFromBackend
        )
          ? "variant"
          : "control",
      };
      setExperimentDetailsPayload(experimentDetails);

      const travellerDetails: TravellerDetails = {
        traveller: generateTravelerDetails(
          formData,
          organiser ?? undefined,
          !!isAddOrganizerToTravellerList && !!organiser?.travelling
        ),
      };

      if (quoteResponse?.referenceId) {
        modifyPayload({
          ...quoteResponse,
          attributes: {
            ...quoteResponse.attributes,
            tripDetails,
            travellerDetails,
            organiser: organiser ?? undefined,
            marketingChannelDetails:
              quoteResponse?.attributes?.marketingChannelDetails,
          },
        });
      } else {
        modifyPayload({
          ...payload,
          attributes: {
            ...payload.attributes,
            tripDetails,
            travellerDetails,
            organiser: organiser ?? undefined,
            marketingChannelDetails:
              quoteResponse?.attributes?.marketingChannelDetails,
          },
        });
      }
    } else {
      setSelectedState(false);
      setIsFormSubmit(true);
    }
  };

  useEffect(() => {
    if (newMedicalFlowEnabled) {
      if (medicalTermsAndConditions === undefined) {
        setHasMedicalConditions(undefined);
        setHasMedicalConditionState(undefined);
      } else {
        setHasMedicalConditions(!medicalTermsAndConditions);
        setHasMedicalConditionState(!medicalTermsAndConditions);
      }
    }
  }, [
    medicalTermsAndConditions,
    newMedicalFlowEnabled,
    setHasMedicalConditionState,
  ]);

  const handleMedicalQuestion = (answer: number) => {
    if (answer && newMedicalFlowEnabled) {
      const hasCondition = findAnswerQThreeById(answer)?.value;
      setHasMedicalConditionState(
        hasMedicalConditions === hasCondition ? undefined : hasCondition
      );
      setHasMedicalConditions((prev) =>
        prev === hasCondition ? undefined : hasCondition
      );
    }
  };

  useEffect(() => {
    if (newMedicalFlowEnabled) {
      // if new medical flow enabled
      const updatedMedicalTermsAndConditions =
        hasMedicalConditions === undefined ? undefined : !hasMedicalConditions;
      if (
        payload.attributes.travellerDetails?.medicalTermsAndConditions !==
        updatedMedicalTermsAndConditions
      ) {
        // if medical conditions updated
        const updatedPayload = { ...payload };
        if (updatedPayload.attributes?.travellerDetails) {
          updatedPayload.attributes.travellerDetails.medicalTermsAndConditions =
            updatedMedicalTermsAndConditions;
          modifyPayload(updatedPayload);
        }
      }
    }
  }, [payload, hasMedicalConditions, newMedicalFlowEnabled]);

  const hasOldTravellersChanged = (
    oldTravellerList: QuoteTraveller[],
    newTravelerList: Traveller[]
  ): boolean => {
    const newTravelerMap = new Map(
      newTravelerList
        .filter((traveller) => traveller.encryptedId)
        .map((traveller) => [traveller.encryptedId, traveller])
    );

    for (const oldTraveler of oldTravellerList) {
      const isOrganizerToIgnore =
        sessionStorage.getItem("organiserTravellerId") ===
          oldTraveler.encryptedId && oldTraveler.sequence === 1;

      if (!isOrganizerToIgnore) {
        const newTraveler = newTravelerMap.get(oldTraveler.encryptedId);

        // Case 1: Traveller is removed
        if (!newTraveler) {
          return true;
        }

        // Case 2: Name changed
        if (
          oldTraveler.firstName !== newTraveler.firstName ||
          oldTraveler.lastName !== newTraveler.lastName
        ) {
          return true;
        }
      }
    }
    return false; // No changes or removals found
  };

  const handleSubmitButton = async () => {
    if (
      (organiserEdit || editPage) &&
      userSavedTraveller &&
      userSavedTraveller.length > travelersLimit
    ) {
      setIsOpenTravelerExceedModal(true);
      return;
    }

    const isMedicalQuestionValid =
      !newMedicalFlowEnabled || hasMedicalConditions === false;
    const isFormComplete = !isFormSubmit && isMedicalQuestionValid;

    const canProceed = current?.brandSpecificParams?.isTrafficLightEnabled
      ? isFormComplete
      : !isFormSubmit;

    if (canProceed) {
      setSubmitBtnClicked(true);
      setButtonLoading(true);
      if (isTurnstileEnabled && !!quoteResponse?.turnstileToken) {
        const currentTurnstileToken = getCurrentToken();
        dispatch(setTurnstileToken(currentTurnstileToken));
      }
      try {
        if ((editPage || organiserEdit) && quoteResponse?.referenceId) {
          dispatch(setQuestionData(payload));
          if (application === RenewalPage) {
            router.push(current.edges.editFlowRenewalNext ?? "");
          } else {
            const travellerAddPath = editPage
              ? current.edges.toTrvHealthTrvEditNext
              : current.edges.toTrvHealthOrgEditNext;
            router.push(travellerAddPath ?? "");
          }
        } else {
          const result = await dispatch(
            !editPage && quoteResponse?.referenceId
              ? updateQuoteOnQuestion({
                  payload: {
                    ...payload,
                    viewPage: Routes.Traveller,
                    // Add experimentDetails for updateQuoteOnQuestion API
                    ...(current?.brandSpecificParams?.enableExperimentData &&
                      experimentDetailsPayload && {
                        attributes: {
                          ...payload.attributes,
                          experimentDetails: experimentDetailsPayload,
                        },
                      }),
                  },
                  dispatch,
                  router,
                })
              : createQuoteOnQuestion({
                  payload: {
                    ...payload,
                    viewPage: Routes.Traveller,
                    // Add experimentDetails for createQuoteOnQuestion API
                    ...(current?.brandSpecificParams?.enableExperimentData &&
                      experimentDetailsPayload && {
                        attributes: {
                          ...payload.attributes,
                          experimentDetails: experimentDetailsPayload,
                        },
                      }),
                  },
                  dispatch,
                  router,
                })
          );

          const originalPromiseResult = unwrapResult(result);
          if (originalPromiseResult) {
            if (newMedicalFlowEnabled) {
              router.push(current.edges.toMedicalConfNext ?? "");
            } else {
              router.push(current.edges.toTrvHealthNext ?? "");
            }
            sessionStorage.removeItem("initialLoad");
            sessionStorage.removeItem("organiserTravellerId");
          }
        }
        setButtonLoading(false);
      } catch (error) {
        setButtonLoading(false);
      }
    } else {
      setShowErrors(true);
      setValidationTrigger((prev) => prev + 1);
    }
  };

  const toLocationData = selectLoationHookFlash(
    payload?.attributes?.tripDetails as TripDetails
  );

  useGtmQuestionSet({
    pageTitle: QuestionEight,
    tripType: payload?.attributes?.tripDetails?.policyType,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    tripExtra: payload?.attributes?.tripDetails?.goingOnCruise
      ? QuestionFour
      : undefined,
    coverStartDate: payload?.attributes?.tripDetails?.startDate || undefined,
    coverEndDate: payload?.attributes?.tripDetails?.endDate || undefined,
    cruise: payload?.attributes?.tripDetails?.goingOnCruise ?? undefined,
    travelFrom: payload?.attributes?.tripDetails?.fromLocation || undefined,
    travelGroupType: payload?.attributes?.tripDetails?.partyType || undefined,
    isSubmitBtnClicked,
  });

  const handleBackButton = () => {
    let path: string | undefined = current.prev ?? "";
    if (editPage || organiserEdit) {
      // Check if user came from quote-review (sessionStorage is set)
      const editSection = getQuoteReviewSummaryEditSection();
      if (editSection?.startsWith("traveller-")) {
        // User came from quote-review, navigate to quote-review
        path = current.edges.travellerEditQuoteSummaryPrev ?? "";
      } else {
        // User came from quote page, use old flow
        path = editPage
          ? current.edges.toOrganiserTrvEditNext
          : current.edges.toOrganiserOrgEditNext;
      }
    }
    router.push(path ?? "");
  };

  const handleCancelEdit = () => {
    setEditModal(true);
  };

  const isTurnstileTokenAvailable = useMemo(
    () => !!quoteResponse?.turnstileToken,
    [quoteResponse?.turnstileToken]
  );

  const [hasDateError, setHasDateError] = useState<boolean>(false);

  const triggerHelpOverlayOpen = useCallback(() => {
    dispatch(openHelpOverlay());
  }, [dispatch]);

  const renderTravelerExceedingModal = useMemo(() => {
    const count = organiser?.travelling ? travelersLimit + 1 : travelersLimit;
    const modalTitle = t(
      "traveller_exceed_modal_title",
      "Maximum number of travellers reached"
    );
    const modalDescription = t(
      "traveller_exceed_modal_description",
      `We are unable to cover more than ${count} travellers online. If you require cover for more than ${count} travellers, please call us on 0330 822 5939.`
    ).replaceAll("{count}", count.toString());
    const continueButtonLabel = t(
      "traveller_exceed_modal_continue",
      "Continue"
    );
    const contactUsButtonLabel = t(
      "traveller_exceed_modal_contact_us",
      "Contact us"
    );

    return (
      <ConfirmationsModal
        isOpen={isOpenTravelerExceedModal}
        handleConfirmBtnClick={() => {
          setIsOpenTravelerExceedModal(false);
        }}
        handleCancelmBtnClick={() => {
          setIsOpenTravelerExceedModal(false);
          showMessenger(triggerHelpOverlayOpen);
        }}
        modalTitle={modalTitle}
        modalDescription={modalDescription}
        ctaButtonLabel={continueButtonLabel}
        backButtonLabel={contactUsButtonLabel}
      />
    );
  }, [
    organiser?.travelling,
    travelersLimit,
    t,
    isOpenTravelerExceedModal,
    triggerHelpOverlayOpen,
  ]);

  const renderQuestionOneSection = () => {
    const questionTextFilter =
      organiser?.travelling === undefined
        ? ""
        : organiser?.travelling
        ? questionText
        : questionTwoText;
    return (
      <div
        data-testid="traveller-container-id"
        className=" md:ml-0 md:mr-0 ml-[-16px] mr-[-16px]"
      >
        {" "}
        <h1
          className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
                md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
          ref={inputRef}
        >
          {questionTextFilter}
        </h1>
        <QuestionBodySection
          clickedFormTitle={() => smoothScrollToRef(inputRef, 100)}
          tripDetails={{}}
          inputOnePlaceholder={inputOnePlaceholder}
          inputTwoPlaceholder={inputTwoPlaceholder}
          removeButtonLabel={removeButtonLabel}
          addButtonLabel={addButtonLabel}
          formNo={travelFormData?.formCount}
          multipleForm={travelFormData?.multipleForm}
          handleSubmitButton={accessFormData}
          inputOneErrorNumericvalues={inputOneErrorNumericvalues}
          inputOneErrorSpecialCharacters={inputOneErrorSpecialCharacters}
          inputOneErrorMaximumNumber={inputOneErrorMaximumNumber}
          inputTwoErrorNumericvalues={inputTwoErrorNumericvalues}
          inputTwoErrorSpecialCharacters={inputTwoErrorSpecialCharacters}
          inputTwoErrorMaximumNumber={inputTwoErrorMaximumNumber}
          dobMinAge={dobMinAge}
          dobMaxAge={dobMaxAge}
          popupMessage={popupMessage}
          dobPlaceholder={dobPlaceholder}
          travellerType={travellerType}
          coupleMinAge={coupleMinAge}
          personalStatusPlaceholder={personalStatusPlaceholder}
          nationalityPlaceholder={nationalityPlaceholder}
          nationalityPopularCountryList={getNationalityPopularCountryList(
            nationalityPopularCountryList
          )}
          ageParentFamily={ageParentFamily}
          ageFamilyFullTimeEducation={ageFamilyFullTimeEducation}
          ageGroupUnderTwo={ageGroupUnderTwo}
          travellerDetailsEdit={userSavedTraveller}
          organizer={organiser ?? undefined}
          ageSingleParentFamily={ageSingleParentFamily}
          ageSingleParentFullTimeEducation={ageSingleParentFullTimeEducation}
          errorObjEdited={errorObjEdited}
          newMedicalFlowEnabled={newMedicalFlowEnabled}
          hasMedicalConditions={hasMedicalConditions}
          handleMedicalQuestion={handleMedicalQuestion}
          medicalQuestionText={medicalQuestionText ?? ""}
          medicalQuestionInfoText={medicalQuestionInfoText ?? ""}
          medicalServiceDeniedText={medicalServiceDeniedText ?? ""}
          medicalYesBtnText={medicalYesBtnText ?? ""}
          medicalNoBtnText={medicalNoBtnText ?? ""}
          travelersLimit={travelersLimit}
          onTravellerDobValidationTrigger={(hasError) =>
            setHasDateError(hasError)
          }
          identificationTypePlaceholder={identificationTypePlaceholder}
          identificationNumberPlaceholder={identificationNumberPlaceholder}
          identificationTypeRequiredError={identificationTypeRequiredError}
          identificationNumberInvalidError={identificationNumberInvalidError}
          identificationNumberWarning={identificationNumberWarning}
          identificationTypeDNILabel={identificationTypeDNILabel}
          identificationTypeNIELabel={identificationTypeNIELabel}
          identificationTypePassportLabel={identificationTypePassportLabel}
          travellerAlertMessageIncompleteSectionSingle={
            travellerAlertMessageIncompleteSectionSingle
          }
          travellerAlertMessageIncompleteSection={
            travellerAlertMessageIncompleteSection
          }
          travellerAlertMessageLinkSectionSingle={
            travellerAlertMessageLinkSectionSingle
          }
          travellerAlertMessageLinkSection={travellerAlertMessageLinkSection}
          showErrors={showErrors}
          validationTrigger={validationTrigger}
        />
      </div>
    );
  };

  const handleUnSavedWarningMessageContinueBtn = () => {
    setIsOpenUnsavedChangesModal(false);
    resetQuestionData();
    router.push("/");
  };

  return (
    <div
      data-testid="traveller-test-id"
      className={`rounded-radius-xxl 
        bg-surface-neutral-option-1 rounded-b-radius-xxl  
        md:pt-spacing-xxl md:pb-spacing-xxl pt-spacing-m pb-0 px-spacing-m h-full`}
    >
      <div className="lg:w-[414px] md:w-[400px] w-full mx-auto relative h-full flex flex-col justify-between">
        {(editPage || organiserEdit) && application !== RenewalPage && (
          <div>
            <LabelButtonWrapper
              hierarchy="tertiary"
              label={cancelEditingBtnText ?? "Cancel editing"}
              id="cancel-edit-travellers"
              className={`${editQuoteStyle.cancelEditButton}`}
              onClick={handleCancelEdit}
            />
            <ConfirmationsModal
              isOpen={editModal}
              handleConfirmBtnClick={() => {
                resetQuestionData();
                // Check if edit originated from quote-review page
                const editSection = getQuoteReviewSummaryEditSection();
                if (editSection) {
                  // Navigate back to quote-review page
                  router.push(current.edges.cancelQuoteReviewSummaryEdit ?? "");
                } else {
                  // Use default cancelEdit edge (navigate to quote page)
                  router.push(current.edges.cancelEdit ?? "");
                }
              }}
              handleCancelmBtnClick={() => {
                setEditModal(false);
              }}
            />
          </div>
        )}

        {application === RenewalPage &&
          pageContent?.backToRenewalsText &&
          editPage && (
            <div>
              <BackToRenewal
                label={pageContent?.backToRenewalsText}
                className="pb-[0px] 
        top-[-2px] !px-[0px] mb-spacing-l md:!mb-spacing-l lg:mx-[0px]"
                onClick={() => {
                  setIsOpenUnsavedChangesModal(true);
                }}
              />
            </div>
          )}
        <UnSavedChangesWarningMessage
          isOpen={isOpenUnsavedChangesModal}
          onContinueClick={handleUnSavedWarningMessageContinueBtn}
          setIsOpen={setIsOpenUnsavedChangesModal}
        />

        <ValidateMedicalsWarningMessage
          isOpen={isValidateMedicalModalOpen}
          onContinueClick={() => {
            setIsValidateMedicalModalOpen(false);
            handleSubmitButton();
          }}
          setIsOpen={setIsValidateMedicalModalOpen}
        />

        <TransformAnimation animationRef={formRef} currentPage={usagePage}>
          {renderQuestionOneSection()}
          {renderTravelerExceedingModal}
        </TransformAnimation>

        <FadeAnimation animationRef={ctaRef}>
          <div id="traveller-main-cta-button-id">
            <ActionButton
              page={usagePage}
              ctaButtonText={ctaButtonText}
              disableMain={(() => {
                let isValidationDisabled = false;
                if (current?.brandSpecificParams?.isTrafficLightEnabled) {
                  if (newMedicalFlowEnabled && hasMedicalConditions === true) {
                    isValidationDisabled = true;
                  }
                } else if (newMedicalFlowEnabled) {
                  isValidationDisabled = !(
                    !isFormSubmit && hasMedicalConditions === false
                  );
                } else {
                  isValidationDisabled = isFormSubmit;
                }

                return (
                  isValidationDisabled ||
                  (!editPage &&
                    !organiserEdit &&
                    isTurnstileEnabled &&
                    !isTurnstileTokenAvailable) ||
                  (current?.brandSpecificParams?.isTrafficLightEnabled
                    ? false
                    : hasDateError)
                );
              })()}
              isLoading={buttonLoading}
              submitButtonClick={() => {
                if (
                  application === RenewalPage &&
                  attributes?.travellerDetails?.traveller &&
                  payload.attributes?.travellerDetails?.traveller &&
                  hasOldTravellersChanged(
                    attributes?.travellerDetails?.traveller,
                    payload.attributes?.travellerDetails?.traveller
                  )
                ) {
                  setIsValidateMedicalModalOpen(true);
                } else {
                  handleSubmitButton();
                }
              }}
              backButtonClick={handleBackButton}
            />
          </div>
        </FadeAnimation>
      </div>
    </div>
  );
}
export default withMedicalFlow(withApplication(TravellerContainer));
